//NOTE: This widget's definition is included in powerSchoolDesignSystemToolkit.ts
import { PdsAbstractInputWidget } from './pds-abstract-input-widget';
import { BasicValidationRules } from 'common-utils/interfaces';
import { pdsValidationRulesEngine } from './pds-validation-rules-engine';
import { CheckboxOption } from './interfaces/checkbox-option';
import { PdsCheckboxWidget as PdsCheckboxWidgetInterface } from './interfaces/pds-checkbox-widget';
import { pdsLogger } from './pds-logger';
import { pdsI18n } from './pds-i18n';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('input-widgets'), 'pds-checkbox-widget.ts');

export class PdsCheckboxWidget extends PdsAbstractInputWidget implements PdsCheckboxWidgetInterface {

  is = 'pds-checkbox-widget';

  //Options
  dataInputType: string;

  //Used to cache the regex so we don't keep re-creating it.
  validationRegex: RegExp;

  options: CheckboxOption[];

  scopeClass: string;

  properties = {
    //AbstractInputWidget Properties
    name: String, //We construct the id using the name property
    dataLabelText: String,
    dataShowLabel: {
      type: String,
      value: null
    },
    dataBadgeText: {
      type: String,
      value: '',
      observer: 'setUpBadge'
    },
    dataHelperText: {
      type: String,
      value: null
    },
    dataFieldHelpText: String,
    dataTooltipText: String,
    dataUseAlternateColorScheme: {
      type: String,
      value: 'false'
    },
    dataIsfieldonly: {
      //This is for adding a class to get rid of the bottom padding.
      type: String,
      value: 'false'
    },
    dataHideUnelevatedBadge: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataDisableInvalidBadgeElevation: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    disabled: {
      type: String,
      value: 'false',
      observer: 'updateDisabled'
    },
    dataIsrequired: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    //Option to activate the validation indicator
    dataUseValidIndicator: {
      type: String,
      value: 'false'
    },
    dataCustomvalidate: {
      type: String
    },
    options: {
      type: Array,
      value: null,
      observer: 'renderInputs'
    }
  };

  //Array of checked values
  private _modelValue: Array<string>;

  //Get the checked inputs in the DOM and create a comma delimited list of values
  get value(): any {
    let values: Array<string> = [];
    let result = this.querySelectorAll('input:checked');
    for (let i = 0; i < result.length; i++) {
      values.push(result[i].getAttribute('value'));
    }
    if (values.length === 0) {
      return '';
    }
    return JSON.stringify(values);
  }

  set value(value) {
    this.setValue(value);
    this.setReadonlyText();
  }

  get modelValue(): any {
    //In the Angular 2+ event listener, we grab event.target.modelValue
    return this._modelValue;
  }

  set modelValue(value) {
    let modelValueChanged = this.setModelValue(value);
    if (modelValueChanged) {
      this.validateWidget();
      if (this.isValid && this.myErrorField != null) {
        this.showErrorMessage(false);
      }
    }
    this.setReadonlyText();
  }

  generateValidationRules(): void {
    let validationRules: BasicValidationRules = pdsValidationRulesEngine.getBasicValidationRules();
    validationRules.isText = true;
    validationRules.setProperty('isRequired', this.dataIsrequired, Boolean);

    this.validators = pdsValidationRulesEngine.getValidatorsForRules(validationRules);
    if ('dataCustomvalidate' in this) {
      this.validators.push({
        execute: this.pds.validation.getCustomValidate(this.dataCustomvalidate),
        condition: {
          element: this
        }
      });
    }
  }

  redrawOptions(): void {
    log(`Redrawing the options for ${this.name}.`);

    this.redraw('options');

    this.setValue(JSON.stringify(this.modelValue), true);
  }

  /**
   * @param modelValue
   * @param skip
   * @returns returns if the model value was actually set.
   */
  setModelValue(modelValue: any, skip?: boolean): boolean {
    if (modelValue !== this._modelValue) {
      if (modelValue instanceof Array) {
        this._modelValue = [...modelValue];
        if (skip !== true) {
          this.setValue(JSON.stringify(modelValue), true);
        }
      } else {
        delete this._modelValue;
        if (skip !== true) {
          this.setValue('');
        }
      }
      return true;
    }
    return false;
  }

  /**
   *
   * @param value - The string the user typed into the input.
   * @param skip - skips setting the model value. (Keeps from getting into recursive loops)
   */
  setValue(value: string, skip = false): void {
    let values: Array<string> | undefined;

    if (value != null) {
      try {
        values = JSON.parse(value);
      } catch { }
    }

    let inputEls = this.querySelectorAll('input');
    for (let i = 0; i < inputEls.length; i++) {
      let input = inputEls[i];

      input.checked = values != null && values.indexOf(input.value) >= 0;
    }

    if (skip !== true) {
      this.setModelValue(values, true);
    }
  }

  updateDisabled(): void {
    let checkboxes = this.querySelectorAll('input');

    for (let i = 0; i < checkboxes.length; i++) {
      checkboxes[i].disabled = this.isDisabled() === 'disabled';
    }
  }

  //"We use this to manually build the DOM because the repeater in the template was not sufficient for allowing outside users to make changes and re-render."
  renderInputs(): void {
    let container: HTMLElement = <HTMLElement>this.querySelector('ul');
    container.innerHTML = ''; // clear the thing out before adding new stuff

    if (this.options != null) {
      let checkboxIndex = 0;
      for (let option of this.options) {
        // *** Non-Optional tags ***
        //Step 1: Create the elements
        let liEl: HTMLElement = createElement('li', this.scopeClass);
        let labelEl: HTMLElement = createElement('label', this.scopeClass);
        let inputEl: HTMLInputElement = <HTMLInputElement>createElement('input', this.scopeClass);
        let labelTextEl: HTMLElement = createElement('div', this.scopeClass);
        let helpTextEl: HTMLElement = createElement('p', this.scopeClass);

        //Step 2: Set properties, attributes, and add listeners
        labelEl.setAttribute('for', `${this.name}-option-${checkboxIndex}`);

        inputEl.setAttribute('id', `${this.name}-option-${checkboxIndex}`);
        inputEl.setAttribute('type', 'checkbox');
        inputEl.setAttribute('name', this.name);
        inputEl.setAttribute('value', option.value);
        // inputEl.addEventListener('blur', (event) => {
        //   this.focusLost(event);
        // });
        inputEl.addEventListener('change', () => {
          this.inputChange();
        });
        inputEl.disabled = this.isDisabled() === 'disabled';

        labelTextEl.classList.add('pds-label-text');
        labelTextEl.textContent = option.text;

        helpTextEl.classList.add('pds-help-text');
        if (option.helperText == null || option.helperText.length === 0) {
          helpTextEl.classList.add('pds-is-hidden');
        }
        helpTextEl.textContent = option.helperText || null;

        //Step 3: append elements to the DOM
        container.appendChild(liEl);
        liEl.appendChild(labelEl);
        liEl.appendChild(helpTextEl);
        labelEl.appendChild(inputEl);
        labelEl.appendChild(labelTextEl);

        // *** Optional tags ***
        //Next build the optional field badge
        if (option.badgeText) {
          let fieldBadgeEl: HTMLElement = createElement('span', this.scopeClass);
          fieldBadgeEl.classList.add('pds-field-badge');

          fieldBadgeEl.textContent = option.badgeText;

          // add white space between elements to match how the other widgets work - without this the label - badge spacing doesn't match
          labelTextEl.appendChild(document.createTextNode(' '));
          labelTextEl.appendChild(fieldBadgeEl);
        }

        //Next build the optional tooltip text
        if (option.tooltipText) {
          let fieldHelpEl: HTMLElement = createElement('span', this.scopeClass);

          let fieldTooltipEl: HTMLElement = createElement('pds-tooltip', this.scopeClass);
          fieldTooltipEl.textContent = option.tooltipText;

          fieldHelpEl.classList.add('pds-field-help');
          fieldHelpEl.classList.add('pds-tooltip-trigger');

          fieldHelpEl.appendChild(fieldTooltipEl);
          // add white space between elements to match how the other widgets work - without this the badge - tooltip spacing doesn't match
          labelTextEl.appendChild(document.createTextNode(' '));
          labelTextEl.appendChild(fieldHelpEl);
        }

        checkboxIndex++;
      }
      // check the checkbox(es) after its rendered
      this.setValue(JSON.stringify(this._modelValue), true);
    }
  }

  //Overriding pds-abstract-input-widget inputChange method to fix on-blur issues on Mac
  inputChange(): void {
    this.errorPresentWhenBlurOccurred = true;
    super.inputChange();
  }

  //Overriding pds-abstract-input-widget validateWidget method to fix on-blur issues on Mac
  validateWidget(showValidationResult = false): boolean {
    return super.validateWidget(true);
  }
}

function createElement(tagName: string, scopeClass: string): HTMLElement {
  let element: HTMLElement = document.createElement(tagName);
  element.classList.add('pds-checkbox-widget');
  element.classList.add('style-scope');
  if (!!scopeClass) {
    element.classList.add(scopeClass);
  }
  return element;
}
